import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Clock, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import {
  ORDER_STAGE_LABELS,
  SHIPMENT_STAGE_LABELS,
  emptyFilter,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { visibleModules, visibleSections } from "@/auth/visible-navigation";
import {
  GROUP_LIMIT,
  SEARCH_DEBOUNCE_MS,
  closeGlobalSearch,
  matchPages,
  openGlobalSearch,
  opensSearch,
  readRecentSearches,
  rememberSearch,
  searchTerm,
  seeAllLabel,
  useGlobalSearchOpen,
  type ResultKind,
  type SearchTarget,
} from "@/components/search/global-search";
import { isTyping } from "@/components/table/selection";
import { formatMoney } from "@/components/table/cells";
import { formatPhone } from "@/lib/phone";
import { cn } from "@/lib/utils";
import { recordKey } from "@/modules/contacts/directory/projections";
import { withVisibleKinds } from "@/modules/contacts/standing";

interface Row {
  key: string;
  kind: ResultKind;
  title: string;
  detail: string | null;
  target: SearchTarget;
}

interface Group {
  id: string;
  heading: string;
  rows: Row[];
  /** "See all 42 orders", opening the list already searched. */
  seeAll: { label: string; target: SearchTarget } | null;
  pending: boolean;
}

const KIND_TONE: Record<ResultKind, string> = {
  Order: "bg-tint-blue text-tint-blue-foreground",
  Contact: "bg-tint-green text-tint-green-foreground",
  Shipment: "bg-tint-orange text-tint-orange-foreground",
  Product: "bg-tint-pink text-tint-pink-foreground",
  Doc: "bg-tint-yellow text-tint-yellow-foreground",
  Report: "bg-tint-purple text-tint-purple-foreground",
  Page: "bg-muted text-muted-foreground",
};

/**
 * Ctrl+K, ⌘K or "/" opens the panel from anywhere in the shell (#640). "/"
 * only when the person is not typing, so it still types a slash in a field.
 */
function useOpenShortcut() {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!opensSearch(event, isTyping(event.target))) return;
      event.preventDefault();
      openGlobalSearch();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);
}

/**
 * The global search panel (#640), mounted once in the shell.
 *
 * Three small requests at once — orders, contacts, shipments — each the list's
 * own search with a limit of five, so each is checked by its own list's
 * permission on the server; a list the person may not read is not asked and
 * its group is simply absent. Pages and reports are matched here, from the
 * navigation the person can see.
 */
export function GlobalSearch() {
  useOpenShortcut();
  const open = useGlobalSearchOpen();

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) closeGlobalSearch();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/40 motion-reduce:animate-none" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "bg-background fixed z-50 flex flex-col overflow-hidden shadow-lg",
            // A phone: the whole screen. A desktop: a panel near the top.
            "inset-0 sm:inset-auto sm:top-[12vh] sm:left-1/2 sm:max-h-[72vh] sm:w-[min(40rem,calc(100%-2rem))] sm:-translate-x-1/2 sm:rounded-lg sm:border",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Search
          </DialogPrimitive.Title>
          {open ? <SearchPanel /> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function SearchPanel() {
  const data = useDataSource();
  const permissions = usePermissions();
  const navigate = useNavigate();

  const [input, setInput] = useState("");
  const [term, setTerm] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState(readRecentSearches);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  function type(next: string) {
    setInput(next);
    setActive(0);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setTerm(searchTerm(next));
    }, SEARCH_DEBOUNCE_MS);
  }

  const mayOrders = can(permissions, "orders.list.view");
  const mayContacts = can(permissions, "contacts.contact.view");
  const mayShipments = can(permissions, "orders.shipments.view");
  const mayCustomers = can(permissions, "customers.view");
  const mayDocs = can(permissions, "docs.page.view");
  const mayProducts = can(permissions, "catalog.product.view");

  const orders = useQuery({
    queryKey: ["global-search", "orders", term],
    queryFn: () => data.orders.list({ search: term ?? "", limit: GROUP_LIMIT }),
    enabled: term !== null && mayOrders,
  });
  const contacts = useQuery({
    queryKey: ["global-search", "contacts", term, mayCustomers],
    queryFn: () =>
      data.contacts.directory({
        search: term ?? "",
        limit: GROUP_LIMIT,
        filter: withVisibleKinds(mayCustomers, emptyFilter()),
      }),
    enabled: term !== null && mayContacts,
  });
  const shipments = useQuery({
    queryKey: ["global-search", "shipments", term],
    queryFn: () =>
      data.orders.listShipments({ search: term ?? "", limit: GROUP_LIMIT }),
    enabled: term !== null && mayShipments,
  });
  // The catalog list's own search: title, SKU, barcode, vendor and tag (#656).
  const products = useQuery({
    queryKey: ["global-search", "products", term],
    queryFn: () =>
      data.catalog.listProducts({ search: term ?? "", limit: GROUP_LIMIT }),
    enabled: term !== null && mayProducts,
  });
  // Docs' own search, which already keeps to pages the person may read.
  const docs = useQuery({
    queryKey: ["global-search", "docs", term],
    queryFn: () => data.docs.search(term ?? "", { limit: GROUP_LIMIT }),
    enabled: term !== null && mayDocs,
  });

  const modules = useMemo(
    () =>
      visibleModules(permissions).map((module) => ({
        module,
        sections: visibleSections(module, permissions),
      })),
    [permissions],
  );

  const groups: Group[] = [];
  if (term !== null) {
    const see = (to: string, total: number | null, noun: string) =>
      total !== null && total > GROUP_LIMIT
        ? {
            label: seeAllLabel(total, noun),
            target: { to, search: { q: term } },
          }
        : null;

    if (mayOrders) {
      groups.push({
        id: "orders",
        heading: "Orders",
        pending: orders.isPending,
        rows: (orders.data?.items ?? []).map((order) => ({
          key: `order:${order.id}`,
          kind: "Order",
          title: [order.orderNumber, order.customerName]
            .filter(Boolean)
            .join(" · "),
          detail: [formatMoney(order.total), ORDER_STAGE_LABELS[order.stage]]
            .filter(Boolean)
            .join(" · "),
          target: { to: "/orders", search: { record: order.id } },
        })),
        seeAll: see("/orders", orders.data?.total ?? null, "orders"),
      });
    }
    if (mayContacts) {
      groups.push({
        id: "contacts",
        heading: "Contacts",
        pending: contacts.isPending,
        rows: (contacts.data?.items ?? []).map((entry) => ({
          key: `contact:${recordKey(entry)}`,
          kind: "Contact",
          title: entry.displayName || "—",
          detail:
            [
              entry.phone ? formatPhone(entry.phone) : null,
              entry.city,
              entry.kind === "customer" ? "Customer" : entry.companyName,
            ]
              .filter(Boolean)
              .join(" · ") || null,
          target: { to: "/contacts", search: { record: recordKey(entry) } },
        })),
        seeAll: see("/contacts", contacts.data?.total ?? null, "contacts"),
      });
    }
    if (mayShipments) {
      groups.push({
        id: "shipments",
        heading: "Shipments",
        pending: shipments.isPending,
        rows: (shipments.data?.items ?? []).map((row) => ({
          key: `shipment:${row.id}`,
          kind: "Shipment",
          title: row.trackingNumber ?? row.name ?? row.orderNumber,
          detail:
            [
              row.orderNumber,
              row.courier ?? row.courierKey,
              row.stage ? SHIPMENT_STAGE_LABELS[row.stage] : null,
            ]
              .filter(Boolean)
              .join(" · ") || null,
          // The parcel's order, where its tracking lives.
          target: { to: "/orders", search: { record: row.orderId } },
        })),
        seeAll: see(
          "/orders/shipments",
          shipments.data?.total ?? null,
          "shipments",
        ),
      });
    }
    if (mayProducts) {
      groups.push({
        id: "products",
        heading: "Products",
        pending: products.isPending,
        rows: (products.data?.items ?? []).map((product) => ({
          key: `product:${product.id}`,
          kind: "Product",
          title: product.title,
          detail:
            [product.vendor, product.productType, product.status]
              .filter(Boolean)
              .join(" · ") || null,
          target: { to: "/catalog", search: { record: product.id } },
        })),
        seeAll: see("/catalog", products.data?.total ?? null, "products"),
      });
    }
    if (mayDocs) {
      groups.push({
        id: "docs",
        heading: "Docs",
        pending: docs.isPending,
        rows: (docs.data?.items ?? []).map((page) => ({
          key: `doc:${page.id}`,
          kind: "Doc",
          title: page.title,
          detail: null,
          target: { to: "/docs", search: { record: page.id } },
        })),
        seeAll: null,
      });
    }
    const pages = matchPages(modules, term);
    const reports = pages.filter((page) => page.kind === "Report");
    const others = pages.filter((page) => page.kind === "Page");
    for (const [id, heading, list] of [
      ["reports", "Reports", reports],
      ["pages", "Pages", others],
    ] as const) {
      groups.push({
        id,
        heading,
        pending: false,
        rows: list.slice(0, GROUP_LIMIT).map((page) => ({
          key: `${id}:${page.target.to}`,
          kind: page.kind,
          title: page.label,
          detail: page.detail,
          target: page.target,
        })),
        seeAll: null,
      });
    }
  }

  const shown = groups.filter((group) => group.rows.length > 0);
  const flat: { target: SearchTarget }[] = shown.flatMap((group) => [
    ...group.rows,
    ...(group.seeAll ? [group.seeAll] : []),
  ]);
  const waiting = groups.some((group) => group.pending);

  function go(target: SearchTarget) {
    if (term !== null) {
      rememberSearch(term);
      setRecent(readRecentSearches());
    }
    closeGlobalSearch();
    void navigate({ to: target.to, search: target.search ?? {} });
  }

  let index = -1;
  const item = (target: SearchTarget, children: ReactNode, key: string) => {
    index += 1;
    const mine = index;
    return (
      <li key={key}>
        <button
          type="button"
          data-active={active === mine}
          onMouseMove={() => {
            setActive(mine);
          }}
          onClick={() => {
            go(target);
          }}
          className="data-[active=true]:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left"
        >
          {children}
        </button>
      </li>
    );
  };

  return (
    <>
      <div className="flex items-center gap-2 border-b px-4">
        <Search aria-hidden className="text-muted-foreground size-4 shrink-0" />
        <input
          autoFocus
          value={input}
          onChange={(event) => {
            type(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActive((current) => Math.min(current + 1, flat.length - 1));
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActive((current) => Math.max(current - 1, 0));
            } else if (event.key === "Enter") {
              const chosen = flat[active];
              if (chosen) {
                event.preventDefault();
                go(chosen.target);
              }
            }
          }}
          placeholder="Search orders, products, contacts, shipments, docs, pages"
          aria-label="Search everything"
          className="placeholder:text-muted-foreground h-14 min-w-0 flex-1 bg-transparent text-base outline-none sm:h-12 sm:text-sm"
        />
        <kbd className="text-muted-foreground hidden rounded border px-1.5 py-0.5 text-[11px] sm:inline">
          Esc
        </kbd>
        <DialogPrimitive.Close
          aria-label="Close search"
          className="text-muted-foreground hover:bg-accent -mr-2 rounded-md p-2 sm:hidden"
        >
          <X className="size-5" aria-hidden />
        </DialogPrimitive.Close>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-2">
        {term === null ? (
          recent.length > 0 ? (
            <section aria-label="Recent searches">
              <h3 className="text-muted-foreground px-4 py-1.5 text-xs font-medium">
                Recent searches
              </h3>
              <ul>
                {recent.map((past) => (
                  <li key={past}>
                    <button
                      type="button"
                      onClick={() => {
                        setInput(past);
                        setTerm(searchTerm(past));
                      }}
                      className="hover:bg-accent flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left text-sm"
                    >
                      <Clock
                        aria-hidden
                        className="text-muted-foreground size-4"
                      />
                      {past}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">
              Type two or more letters or digits. A SKU or product name finds
              the product; the last digits of a phone number find the order and
              the contact.
            </p>
          )
        ) : shown.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm">
            {waiting ? "Searching…" : `Nothing found for “${term}”.`}
          </p>
        ) : (
          shown.map((group) => (
            <section key={group.id} aria-label={group.heading} className="pb-2">
              <h3 className="text-muted-foreground px-4 py-1.5 text-xs font-medium">
                {group.heading}
              </h3>
              <ul>
                {group.rows.map((row) =>
                  item(
                    row.target,
                    <>
                      <span
                        className={cn(
                          "w-[4.5rem] shrink-0 rounded px-1.5 py-0.5 text-center text-[11px] font-medium",
                          KIND_TONE[row.kind],
                        )}
                      >
                        {row.kind}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {row.title}
                        </span>
                        {row.detail ? (
                          <span className="text-muted-foreground block truncate text-xs tabular-nums">
                            {row.detail}
                          </span>
                        ) : null}
                      </span>
                    </>,
                    row.key,
                  ),
                )}
                {group.seeAll
                  ? item(
                      group.seeAll.target,
                      <span className="text-primary-strong pl-[5.25rem] text-sm">
                        {group.seeAll.label}
                      </span>,
                      `${group.id}:all`,
                    )
                  : null}
              </ul>
            </section>
          ))
        )}
      </div>
    </>
  );
}
